import { z } from "zod";
import { sanitizeNative } from "@/lib/sanitize";
import { validateImage } from "./image";

/**
 * Common validation utilities for schema validation
 * Extracted to reduce duplication and improve maintainability
 */

/**
 * Sanitizes a string to prevent XSS attacks
 * Removes all HTML tags and attributes from user input
 * @param value - The string to sanitize
 * @returns The sanitized string
 */
export function sanitizeText(value: string): string {
  return sanitizeNative(value);
}

/**
 * Validates a numeric amount field that can be string or number input
 * Transforms string to number and validates range
 */
export function createAmountValidator(
  fieldName: string,
  min: number,
  max: number,
  minError: string,
  maxError: string,
) {
  return z
    .union([z.string(), z.number()])
    .refine((val) => val !== "" && val !== null && val !== undefined, {
      message: `${fieldName} הוא שדה חובה`,
    })
    .transform((val) => (typeof val === "string" ? Number(val) : val))
    .refine((val) => !isNaN(val) && isFinite(val), {
      message: `${fieldName} חייב להיות מספר תקין`,
    })
    .refine((val) => val >= min, { message: minError })
    .refine((val) => val <= max, { message: maxError });
}

/**
 * Validates a positive amount (greater than 0)
 */
export function createPositiveAmountValidator(
  fieldName: string,
  max: number,
  maxError: string,
) {
  return createAmountValidator(
    fieldName,
    0.01, // Must be positive
    max,
    `${fieldName} חייב להיות חיובי`,
    maxError,
  );
}

/**
 * Validates a non-negative amount (0 or greater)
 */
export function createNonNegativeAmountValidator(
  fieldName: string,
  max: number,
  maxError: string,
) {
  return createAmountValidator(
    fieldName,
    0, // Can be zero
    max,
    `${fieldName} חייב להיות 0 או יותר`,
    maxError,
  );
}

/**
 * Validates an optional string field with max length
 */
export function createOptionalStringField(maxLength: number) {
  return z.string().max(maxLength).trim().optional().default("");
}

/**
 * Validates a visible toggle field (boolean with default)
 */
export function createVisibilityToggle(defaultValue = true) {
  return z.boolean().default(defaultValue);
}

/**
 * Validates an Israeli phone number
 * Normalizes: remove all separators (spaces, hyphens, dots) before validation
 */
export const israeliPhoneSchema = z
  .string()
  .max(20)
  .trim()
  .refine(
    (val) => {
      if (!val || val === "") return true;
      // Normalize: remove all separators (spaces, hyphens, dots)
      const normalized = val.replace(/[\s\-\.]/g, "");
      const israeliPhoneRegex =
        /^(\+972[-\s]?|0)([234589]|5[0-9])[-\s]?\d{3}[-\s]?\d{4}$/;
      return israeliPhoneRegex.test(normalized);
    },
    {
      message:
        "מספר טלפון לא חוקי. פורמט מקובל: 05X-XXX-XXXX או +972-XX-XXX-XXXX",
    },
  )
  .optional();

/**
 * Validates an Israeli ID (9 digits with check digit)
 */
export const israeliIdSchema = z
  .string()
  .max(20)
  .trim()
  .refine(
    (val) => {
      if (!val || val === "") return true;
      const cleanId = val.replace(/[-\s]/g, "");
      if (!/^\d{9}$/.test(cleanId)) return false; // Reject invalid format instead of passing through

      // Check digit validation (Luhn variant)
      let total = 0;
      for (let i = 0; i < 9; i++) {
        const weight = i % 2 === 0 ? 1 : 2;
        let val = parseInt(cleanId[i], 10) * weight;
        if (val > 9) {
          val = Math.floor(val / 10) + (val % 10);
        }
        total += val;
      }

      return total % 10 === 0;
    },
    { message: "מספר תז לא חוקי. יש לבדוק את ספרת הביקורת" },
  )
  .optional();

/**
 * Validates a required name field
 * Note: This is a truly required field - no optional() or empty string fallback
 */
export function createRequiredNameField(fieldName: string, maxLength = 500) {
  return z.string().min(1, `${fieldName} הוא שדה חובה`).max(maxLength).trim();
}

/**
 * Creates a sanitized optional string field with max length
 * Sanitization happens BEFORE validation to prevent XSS
 */
export function createSanitizedOptionalStringField(maxLength: number) {
  return z.preprocess(
    (val) => (typeof val === "string" ? sanitizeText(val) : val),
    z.string().max(maxLength).trim().optional().default(""),
  );
}

/**
 * Creates a sanitized required name field
 * Sanitization happens BEFORE validation to prevent XSS
 * Note: This is a truly required field - no optional() or empty string fallback
 */
export function createSanitizedNameField(fieldName: string, maxLength = 500) {
  return z.preprocess(
    (val) => (typeof val === "string" ? sanitizeText(val) : val),
    z.string().min(1, `${fieldName} הוא שדה חובה`).max(maxLength).trim(),
  );
}

/**
 * Creates a sanitized string with custom validation pipeline
 * Sanitization happens BEFORE other validations
 */
export function createSanitizedStringWithValidation<T extends z.ZodTypeAny>(
  schema: T,
) {
  return z.preprocess(
    (val) => (typeof val === "string" ? sanitizeText(val) : val),
    schema,
  );
}

/**
 * Base64 image validation for logo uploads
 */
export const base64ImageSchema = z
  .string()
  .trim()
  .default("")
  .refine((val) => {
    if (!val) return true;
    // Check format
    if (!/^data:image\/(jpeg|jpg|png|webp);base64,/.test(val)) return false;
    // Check size (2MB max ≈ 2,796,203 chars for base64)
    if (val.length > 2_796_203) return false;
    // Validate base64 characters after prefix
    const base64Part = val.split(",")[1];
    if (!base64Part) return false;
    // Base64 regex: A-Z, a-z, 0-9, +, /, and = padding
    return /^[A-Za-z0-9+/]*={0,2}$/.test(base64Part);
  }, "הלוגו חייב להיות תמונה תקינה (JPEG, PNG או WebP) בפורמט base64, עד 2MB")
  .optional();

/**
 * Async validation for logo image using Bun.Image
 * Validates image metadata (dimensions, format) and checks size limits
 */
export async function validateLogoImage(
  base64Image: string,
): Promise<{
  valid: boolean;
  error?: string;
  width?: number;
  height?: number;
}> {
  if (!base64Image) return { valid: true };

  const result = await validateImage(base64Image);

  if (!result.valid) {
    return {
      valid: false,
      error: result.error || "Invalid image",
    };
  }

  // Check dimensions - max 1000x1000 for logos
  if (result.width && result.height) {
    if (result.width > 1000 || result.height > 1000) {
      return {
        valid: false,
        error: "הלוגו גדול מדי - מקסימום 1000x1000 פיקסלים",
        width: result.width,
        height: result.height,
      };
    }
  }

  return {
    valid: true,
    width: result.width,
    height: result.height,
  };
}
